import { AutoForm, AutoFormInputField, Button } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import { useState } from "react";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import translate from "src/lib/translate";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import UserCubit from "src/state/UserCubit/UserCubit";
import { useBloc } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import Loader from "src/ui/components/Loader/Loader";
import ErrorBox from "src/ui/components/StyledComponents/ErrorBox";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";

const PersonifyHealthEmailSchema = z.object({
  email: z.string().email()
});

type PersonifyHealthEmailData = z.infer<typeof PersonifyHealthEmailSchema>;

const PersonifyHealthEmailStep: FC = () => {
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<TranslationKey>();
  const { headingRef } = useFocusHeading();
  const [, { updatePersonifyHealthEmail }] = useBloc(UserCubit);
  const [, { nextStep }] = useBlocNext(SignupCustomBloc);

  const handleSubmit = (data: PersonifyHealthEmailData) => {
    setLoading(true);
    setErrorCode(undefined);

    void updatePersonifyHealthEmail(data.email)
      .then(({ error }) => {
        if (error) {
          setErrorCode("error_generic");
          return;
        }
        void nextStep();
      })
      .catch(() => setErrorCode("error_generic"))
      .finally(() => setLoading(false));
  };

  return (
    <div
      style={{
        maxWidth: `${APP_CONTENT_WIDTH_WITHOUT_PADDING}px`,
        margin: "0 auto",
        padding: "0 var(--space-md)"
      }}
    >
      <nine-spacer s="xl"></nine-spacer>
      <h1 className="as-h4-large" ref={headingRef} {...getHeadingProps()}>
        <Translate msg="personifyHealth.email.title" />
      </h1>
      <p className="m0 color-c-80">
        <Translate msg="personifyHealth.email.description" />
      </p>

      <nine-spacer s="xl"></nine-spacer>

      <Loader active={loading}>
        <AutoForm schema={PersonifyHealthEmailSchema} onSubmit={handleSubmit}>
          <AutoFormInputField
            name="email"
            label={translate("email")}
            isRequired
            type="text"
            inputMode="email"
            autoComplete="email"
          />
          {errorCode && (
            <>
              <nine-spacer s="sm"></nine-spacer>
              <ErrorBox>
                <Translate msg={errorCode} />
              </ErrorBox>
            </>
          )}
          <nine-spacer s="sm"></nine-spacer>
          <Button type="submit" theme="sunrise" style={{ margin: "auto" }}>
            <Translate msg="continue" />
          </Button>
        </AutoForm>
      </Loader>
    </div>
  );
};

export default PersonifyHealthEmailStep;
